<template>
  <v-tooltip
    opacity="1"
    :interactive="interactive"
    :content-class="computedClass"
    :location="location"
    :max-width="maxWidth"
  >
    <template #activator="{ props }">
      <slot name="activator" :props="props" />
    </template>

    <div class="ag-tooltip__content">
      <div v-if="computedType === 'large'" class="ag-tooltip__head">
        <slot name="prepend-title" />

        <div v-if="$slots.title" class="ag-tooltip__title">
          <slot name="title" />
        </div>
      </div>

      <div class="ag-tooltip__body">
        <div class="ag-tooltip__text" :style="computedStyle">
          <slot name="default" />
        </div>
      </div>
    </div>
  </v-tooltip>
</template>

<script>
/**
 * @template T; @typedef {import("vue").PropType<T>} PropType<T>
 *
 * @typedef {Object} AGTooltipProps
 * @property {'top'|'bottom'|'left'|'right'} [location='top']
 * @property {number} [maxWidth=400]
 * @property {number} [clamp=0]
 * @property {string} [contentClass]
 * @property {boolean} [interactive=false]
 */
export default {
  emits: ['update:modelValue'],
  props: {
    location: {
      type: [String],
      default: 'top',
      validator: (v) => ['top', 'bottom', 'left', 'right'].includes(v),
    },
    maxWidth: {
      type: [Number],
      default: 400,
    },
    clamp: {
      type: [Number],
      default: 0,
    },
    contentClass: {
      type: [String],
      default: undefined,
    },
    interactive: {
      type: [Boolean],
      default: false,
    },
  },
  computed: {
    computedType() {
      return this.$slots.title ? 'large' : 'default';
    },
    computedClass() {
      const classes = ['ag-tooltip', `ag-tooltip--type-${this.computedType}`, this.contentClass];

      if (this.clamp > 0) classes.push('ag-tooltip--clamp');

      return classes.join(' ');
    },
    computedStyle() {
      const style = {};

      if (this.clamp) style['-webkit-line-clamp'] = this.clamp;

      return style;
    },
  },
};
</script>

<style scoped lang="scss">
::v-deep(.ag-tooltip) {
  &.v-overlay__content {
    background: transparent !important;
    border-radius: 8px !important;
    overflow: hidden !important;
    box-shadow: $shadow-level-1;
  }

  &.ag-tooltip--type-default {
    .ag-tooltip__body {
      padding: 4px 8px;
      background-color: $neutral-color-high-medium;
      color: $neutral-color-low-light;
      border-radius: 8px;
    }
  }

  &.ag-tooltip--type-large {
    .ag-tooltip__content {
      .ag-tooltip__head {
        padding: 8px 16px;
        display: flex;
        flex-direction: row;
        align-items: center;
        justify-content: flex-start;
        gap: 8px;
        background-color: $neutral-color-high-medium;
        color: #fff;

        .ag-tooltip__icon {
          margin-right: 8px;
        }

        .ag-tooltip__title {
          font-weight: 500;
          text-box: auto;
        }
      }

      .ag-tooltip__body {
        padding: 16px;
        background-color: $neutral-color-low-dark;
        color: $neutral-color-high-medium;
        border: solid 1px $neutral-color-high-medium;
        border-top: none;
        border-radius: 0 0 8px 8px;
      }
    }
  }

  &.ag-tooltip--clamp {
    .ag-tooltip__text {
      overflow: hidden;
      display: -webkit-box;
      -webkit-box-orient: vertical;
    }
  }
}
</style>
